import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { formatCAD, formatDate } from "@/lib/utils-cart";
import { toast } from "sonner";
import { StatusPill } from "@/components/Badges";

export default function Wallet() {
  const [wallet, setWallet] = useState(null);
  const [tx, setTx] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [form, setForm] = useState({ amount: "", method: "bank", notes: "" });

  const load = () => {
    api.get("/wallet").then((r) => setWallet(r.data));
    api.get("/wallet/transactions").then((r) => setTx(r.data));
    api.get("/payouts/mine").then((r) => setPayouts(r.data));
  };
  useEffect(() => { load(); }, []);

  const request = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payouts", { amount: Number(form.amount), method: form.method, notes: form.notes || undefined });
      toast.success("Payout requested");
      setForm({ amount: "", method: "bank", notes: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <DashboardLayout title="Wallet & Payouts" subtitle="Track earnings, ledger entries, and request payouts.">
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-4">
          <div className="v-card bg-primary text-primary-foreground border-primary">
            <div className="label-eyebrow text-white/60">Available balance</div>
            <div className="font-display font-black text-4xl mt-2">{formatCAD(wallet?.balance || 0)}</div>
            <div className="text-xs text-white/70 mt-1 font-mono">+ {formatCAD(wallet?.pending || 0)} pending · CAD</div>
          </div>

          <form onSubmit={request} className="v-card">
            <h3 className="font-display font-semibold text-primary">Request payout</h3>
            <div className="mt-3 space-y-2">
              <input type="number" step="0.01" placeholder="Amount CAD" className="v-input" value={form.amount} onChange={(e) => setForm({...form, amount: e.target.value})} required data-testid="payout-amount" />
              <select className="v-input" value={form.method} onChange={(e) => setForm({...form, method: e.target.value})} data-testid="payout-method">
                <option value="bank">Bank transfer</option>
                <option value="paypal">PayPal</option>
                <option value="interac">Interac e-Transfer</option>
              </select>
              <textarea rows={2} placeholder="Notes (optional)" className="v-input" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid="payout-notes" />
              <button className="v-btn-primary w-full" data-testid="payout-submit">Request payout</button>
            </div>
          </form>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="v-card">
            <h3 className="font-display text-lg font-semibold text-primary">Recent ledger</h3>
            <div className="mt-3 divide-y divide-border">
              {tx.map((t) => (
                <div key={t.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="capitalize text-primary font-medium">{t.type.replace("_", " ")}</div>
                    <div className="text-xs text-muted-foreground font-mono">{t.order_id ? `Order ${t.order_id.slice(0, 8)}` : "—"} · {formatDate(t.created_at)}</div>
                  </div>
                  <div className={`font-mono ${t.amount < 0 ? "text-destructive" : "text-accent-green"}`}>{t.amount < 0 ? "-" : "+"}{formatCAD(Math.abs(t.amount))}</div>
                </div>
              ))}
              {tx.length === 0 && <div className="py-6 text-center text-muted-foreground text-sm">No transactions yet.</div>}
            </div>
          </div>

          <div className="v-card">
            <h3 className="font-display text-lg font-semibold text-primary">Payout history</h3>
            <div className="mt-3 divide-y divide-border">
              {payouts.map((p) => (
                <div key={p.id} className="py-3 flex items-center justify-between text-sm">
                  <div>
                    <div className="text-primary font-medium">{formatCAD(p.amount)} via {p.method}</div>
                    <div className="text-xs text-muted-foreground font-mono">{formatDate(p.created_at)}</div>
                  </div>
                  <StatusPill tone={p.status === "approved" ? "success" : p.status === "rejected" ? "danger" : "warning"}>{p.status}</StatusPill>
                </div>
              ))}
              {payouts.length === 0 && <div className="py-6 text-center text-muted-foreground text-sm">No payout requests yet.</div>}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
